import { Bot, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { OPEN_ASSISTANT_CHAT_EVENT, type OpenAssistantChatDetail } from "../assistant-events";
import { initialAssistantMessage, loadAssistantHistory, saveAssistantHistory, type AssistantHistory } from "../assistant-history";
import { AskInterface } from "./AskInterface";

type Props = {
  siteKey: string;
  modelLabel: string;
  theme: "light" | "dark";
  componentAiEnabled: boolean;
};

export function FloatingAssistant(props: Props) {
  const [open, setOpen] = useState(false);
  const [chatRequest, setChatRequest] = useState<{ id: number; prompt?: string } | null>(null);
  const [history, setHistory] = useState<AssistantHistory>(() => loadAssistantHistory() ?? {
    conversationId: crypto.randomUUID(),
    messages: [initialAssistantMessage],
  });
  const launcherRef = useRef<HTMLButtonElement>(null);
  const requestId = useRef(0);

  useEffect(() => {
    saveAssistantHistory(history);
  }, [history]);

  useEffect(() => {
    const openRequestedChat = (event: Event) => {
      const detail = (event as CustomEvent<OpenAssistantChatDetail>).detail;
      if (!detail?.prompt.trim()) return;
      requestId.current += 1;
      setChatRequest({ id: requestId.current, prompt: detail.prompt });
      setOpen(true);
    };
    window.addEventListener(OPEN_ASSISTANT_CHAT_EVENT, openRequestedChat);
    return () => window.removeEventListener(OPEN_ASSISTANT_CHAT_EVENT, openRequestedChat);
  }, []);

  const close = () => {
    setOpen(false);
    setChatRequest(null);
    window.setTimeout(() => launcherRef.current?.focus(), 0);
  };

  const openEmptyChat = () => {
    setChatRequest(null);
    setOpen(true);
  };

  return (
    <aside className="assistant-widget" aria-label="AI assistant">
      {open && <AskInterface {...props} key={chatRequest?.id ?? "assistant-widget"} compact autoFocus onClose={close} initialPrompt={chatRequest?.prompt} history={history} onHistoryChange={setHistory} />}
      <button
        ref={launcherRef}
        className="assistant-widget-launcher"
        type="button"
        onClick={() => open ? close() : openEmptyChat()}
        aria-label={open ? "Close assistant chat" : "Open assistant chat"}
        aria-expanded={open}
        aria-controls={open ? "assistant-widget-chat" : undefined}
      >
        {open ? <X size={21} /> : <Bot size={21} />}
        <span className="assistant-widget-tooltip">Ask Omer's AI</span>
      </button>
    </aside>
  );
}
